import { getCurrentUser } from "aws-amplify/auth";
import type {
  DictionaryEntry,
  DictionaryOperationRequest,
  DictionaryApplyRequest,
  DictionaryApplyResponse,
  DictionaryUpsertRequest,
  PaginatedDictionaryResponse,
} from "@/types/dictionary";

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  try {
    await getCurrentUser();
    const { fetchAuthSession } = await import("aws-amplify/auth");
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (idToken) {
      return { "Content-Type": "application/json", Authorization: idToken };
    }
  } catch {
    // 未ログインなど
  }
  return { "Content-Type": "application/json" };
};

const callApi = async (
  requestData: DictionaryOperationRequest | DictionaryApplyRequest | DictionaryUpsertRequest
) => {
  const headers = await getAuthHeaders();
  const response = await fetch("/api/dictionary-handler", {
    method: "POST",
    headers,
    body: JSON.stringify(requestData),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "辞書APIエラー");
  }
  return data;
};

export const fetchDictionary = async (): Promise<DictionaryEntry[]> => {
  const data = await callApi({ operation: "GET" });
  const items: unknown = Array.isArray(data)
    ? data
    : data?.items ?? data?.Items ?? data?.data ?? data?.body?.items ?? data?.body?.Items ?? data?.body?.data;

  if (!Array.isArray(items)) return [];

  const normalized: DictionaryEntry[] = (items as unknown[]).map((raw) => {
    const entry = (raw ?? {}) as Partial<DictionaryEntry> & { incorrect_variants?: unknown };
    const iv = entry.incorrect_variants as unknown;
    let incorrect: string[] = [];
    if (Array.isArray(iv)) {
      incorrect = (iv as unknown[]).map((s) => String(s));
    } else if (typeof iv === "string") {
      try {
        const parsed = JSON.parse(iv);
        if (Array.isArray(parsed)) {
          incorrect = parsed.map((s) => String(s));
        } else {
          incorrect = iv.includes(",") ? iv.split(",").map((s) => s.trim()) : [iv];
        }
      } catch {
        incorrect = iv.includes(",") ? iv.split(",").map((s) => s.trim()) : [iv];
      }
    } else if (iv == null) {
      incorrect = [];
    }
    return {
      id: String(entry.id ?? ""),
      original_word: String(entry.original_word ?? ""),
      reading: entry.reading,
      incorrect_variants: incorrect,
      category: entry.category,
      confidence_score: entry.confidence_score,
      usage_count: entry.usage_count,
      is_active: entry.is_active,
      source: entry.source,
      created_at: entry.created_at,
      updated_at: entry.updated_at,
    } as DictionaryEntry;
  });

  return normalized;
};

export const applyDictionary = async (
  text: string,
  reportId?: string
): Promise<DictionaryApplyResponse> => {
  return await callApi({ operation: "APPLY", text, report_id: reportId });
};

export const upsertDictionary = async (
  entry: Omit<DictionaryEntry, "usage_count" | "created_at" | "updated_at"> & {
    usage_count?: number;
  }
): Promise<string> => {
  const data = await callApi({ operation: "UPSERT", ...entry });
  return data.id as string;
};

export const deleteDictionary = async (id: string, entry_pk?: string): Promise<void> => {
  await callApi({ operation: "DELETE", id, entry_pk });
};

export const fetchPaginatedDictionary = async (): Promise<PaginatedDictionaryResponse> => {
  const items = await fetchDictionary();
  return {
    items,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: items.length,
      itemsPerPage: items.length,
    },
  };
};



